import { useEffect, useRef, useState } from 'react';

import {
  detectSearchMode,
  type PumsemSearchMode,
} from '../lib/pumsemSearchMode';
import { shouldAdoptExternalValue } from '../lib/pumsemSearchSync';

interface PumsemSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}

const MODE_LABEL: Record<PumsemSearchMode, string> = {
  name: '품셈명',
  code: '코드',
};

/**
 * 검색어를 URL 로 올리기 전에 기다리는 시간. 한 글자마다 라우팅하면 조합 중
 * 리렌더가 끼어들고, 서버 질의도 글자 수만큼 나간다.
 */
const PUSH_DELAY_MS = 200;

/**
 * 검색 입력.
 *
 * 모드(품셈명 / 코드)를 화면에 드러내되 **사람이 고르기 전에 입력값 모양으로
 * 먼저 맞춘다.** `6-2-4` 를 치면 코드로, `PE-X 배관` 을 치면 이름으로 붙는다.
 * 모드는 표시일 뿐 서버 파라미터가 아니다 — 서버 정렬이 코드 정확일치를 항상
 * 1등으로 매기므로, 사용자가 본 모드와 결과 순서가 어긋나지 않는다.
 *
 * ── 입력값을 왜 로컬에 들고 있나 (한글 조합) ──────────────────
 * 이 값은 부모의 URL(`?q=`)에 산다. 그런데 `value` 를 그대로 `<input>` 에 물리면
 * 글자 하나마다 라우팅 → 리렌더 → React 가 `input.value` 를 **다시 써넣는다.**
 * 한글은 조합(composition) 중인 글자가 DOM 에 있는데 그걸 덮어쓰면 IME 가
 * 조합을 끊고 다시 시작한다. 그래서 "테스트입니다" 가
 * `ㅌ테텟스슽트틍이입ㄴ니닏다` 로 찍혔다 — 자모마다 한 번씩 확정된 것이다.
 *
 * 그래서 세 가지를 건다.
 *  1) 화면에 보이는 값은 로컬 `draft` — 부모가 늦게 돌려주는 값이 입력을 덮지 않는다.
 *  2) `compositionstart`~`end` 사이에는 위로 올리지 않는다.
 *  3) 올리는 것 자체를 debounce — 라우팅·질의 횟수도 같이 줄어든다.
 *
 * 2번 덕분에 **조합 중에는 `q` 가 바뀔 수 없고**, 그래서 아래 반영 판정은
 * 조합 여부를 따로 보지 않아도 된다(`shouldAdoptExternalValue` 주석 참고).
 *
 * 부모 값이 **밖에서** 바뀌는 경우(뒤로가기, `?q=` 딥링크, 조건 초기화)는 여전히
 * 입력창에 반영해야 하므로, 우리가 올려보낸 값의 메아리와 구분한다.
 */
export function PumsemSearchBar({
  value,
  onChange,
  autoFocus = false,
}: PumsemSearchBarProps) {
  const [draft, setDraft] = useState(value);
  /**
   * 마지막으로 부모에게 올린 값. 되돌아오는 메아리를 걸러내는 기준이다.
   * ref 가 아니라 state 인 이유: 렌더 중에 읽어야 하는데 React Compiler 규칙이
   * 렌더 중 ref 접근을 막는다(`react-hooks/refs`).
   */
  const [pushed, setPushed] = useState(value);
  const composingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // 부모 값이 바뀐 **그 렌더에** 판정한다. effect 로 미루면 한 프레임 동안
  // 옛 값이 보이고, 이 리포는 `react-hooks/set-state-in-effect` 도 켜져 있다.
  const [lastSeen, setLastSeen] = useState(value);
  if (value !== lastSeen) {
    const adopt = shouldAdoptExternalValue({ value, lastSeen, pushed });
    setLastSeen(value);
    if (adopt) setDraft(value);
  }

  // 언마운트 뒤에 타이머가 살아 남아 사라진 화면의 URL 을 바꾸면 안 된다.
  useEffect(
    () => () => {
      if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    },
    [],
  );

  const schedulePush = (next: string) => {
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // 타이머가 조합 도중에 터지면 반쪽짜리 글자가 올라간다.
      // 건너뛰어도 `compositionend` 가 다시 예약한다.
      if (composingRef.current) return;
      // 올린 값을 먼저 기록한다. 같은 배치라 부모 값이 돌아올 때는 이미
      // 최신이고, 그래서 메아리가 입력창을 덮지 않는다.
      setPushed(next);
      onChange(next);
    }, PUSH_DELAY_MS);
  };

  // 모드는 화면에 보이는 값에서 파생된다(부모 값이 아니라).
  const mode = detectSearchMode(draft);

  return (
    <div className="flex w-full items-stretch">
      <div
        className="flex shrink-0 items-center rounded-l-xl border border-r-0 border-neutral-200 bg-neutral-50 px-3 text-[14px] font-bold text-neutral-500 dark:border-white/12 dark:bg-white/[0.04] dark:text-neutral-300"
        aria-live="polite"
      >
        {MODE_LABEL[mode]}
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-r-xl border border-neutral-200 bg-white px-3.5 py-2 focus-within:border-brand-500 dark:border-white/12 dark:bg-dark-card">
        <span aria-hidden className="text-neutral-400">
          ⌕
        </span>
        <input
          type="search"
          value={draft}
          autoFocus={autoFocus}
          onCompositionStart={() => {
            composingRef.current = true;
          }}
          onCompositionEnd={(event) => {
            composingRef.current = false;
            // `compositionend` 는 마지막 `change` 보다 **뒤에** 올 수 있다.
            // 여기서 값을 한 번 더 확정해야 끝 글자가 누락되지 않는다.
            const next = event.currentTarget.value;
            setDraft(next);
            schedulePush(next);
          }}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            // 조합 중에는 올리지 않는다. 확정은 compositionend 가 한다.
            if (!composingRef.current) schedulePush(next);
          }}
          placeholder="공종 · 코드 · 직종 · 자재명"
          aria-label="품셈 검색"
          className="min-w-0 flex-1 bg-transparent text-[16px] text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-neutral-100"
        />
      </div>
    </div>
  );
}
